import { sortBy } from '@/lib/sortBy';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { defaultMeta } from '@/constants/defaultMeta';
import { locationQueries } from '@/services/locations/locations.queries';
import { paymentOverviewQueries, salesBreakdownQueries } from '@/services/payment/overview.queries';
import type { PaymentOverviewAnalyticsParams, PaymentSalesBreakdownParams } from '@/services/payment/payment.types';
import { useSearch } from '@tanstack/react-router';

export const usePaymentOverview = () => {
  const { data, isPending } = useQuery(paymentOverviewQueries.overview());

  return {
    paymentOverview: data?.data,
    isLoading: isPending,
  };
};

export const useSalesBreakdown = (params: Pick<PaymentSalesBreakdownParams, 'year'>) => {
  const { sort, page, fetch } = useSearch({ from: '/(app)/payment-management/overview/' });
  const effectiveSort = sort ?? [{ id: 'month', desc: true }];
  const { data, isPending } = useQuery({
    ...salesBreakdownQueries.list({
      ...params,
      row: page ?? 0,
      fetch: fetch ?? 10,
    }),
    select: (res) => ({
      ...res,
      data: sortBy(res.data, effectiveSort),
    }),
  });

  return {
    salesBreakdownData: data?.data || [],
    pagination: data?.meta?.pagination ?? defaultMeta,
    isLoading: isPending,
  };
};

export const usePaymentAnalytics = (params: PaymentOverviewAnalyticsParams) => {
  const { data, isPending } = useQuery(paymentOverviewQueries.analytics(params));

  return {
    paymentAnalytics: data?.data,
    isLoading: isPending,
  };
};

export const usePaymentReservationChart = (params: PaymentOverviewAnalyticsParams) => {
  const { data, isPending } = useQuery(paymentOverviewQueries.reservationChart(params));

  return {
    reservationChart: data?.data,
    isLoading: isPending,
  };
};

export const usePaymentOverviewLocations = () => {
  const { data, isPending } = useQuery(locationQueries.list({ fetch: 100, row: 0, search: '' }));

  return {
    locations: data?.data ?? [],
    isLoading: isPending,
  };
};
